import { Typography, Box, Tooltip, Link } from '@mui/material'
import ColumnKYC from './ColumnDetailKyc'
import { toLocaleUTCDateString, buildTimeStringFromTimestamp } from '../time/timeHelper'
import { Status } from '../../utilities/Status'
import { RiskLevel } from '../../utilities/RiskLevel'
import { Link as ReactRouterLink } from 'react-router-dom'
import { Flags } from '../flag/flags'
import { kybCreationOriginOptions } from '../../../pages/kyb/kyb.enums'
import { ShowMoreListCell, type TColumn } from './ColumnsByCountry'
import { useSelector } from 'react-redux'
import MetadataCell from './MetadataCell'
import { useTranslation } from 'react-i18next'

export const useCommonColumnsKyb = (t: any): TColumn[] => {
  const { currentCountry } = useSelector((state: any) => state.kybCountry)
  const allColumns: TColumn[] = [
    {
      field: 'documentNumber',
      headerName: t('common:documentNumber'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 200,
      sortable: true,
      renderCell: ({ row }) => (
        <Link
          component={ReactRouterLink}
          title={row._id}
          to={`/kyb/${currentCountry}/detail/${row._id}`}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2" fontWeight="bold" sx={{ textAlign: 'center', width: '100%' }}>
            {row.companyDetails?.documentNumber?.includes('kyb.documentNumber.notFound') 
              ? `⚠️${t('messages:kyb.documentNumber.notFound')}⚠️` 
              : row.companyDetails?.documentNumber || '---'
            }
          </Typography>
        </Link>
      ),
    },
    {
      field: 'name',
      headerName: t('common:companyName'),
      headerAlign: 'center',
      minWidth: 350,
      maxWidth: 650,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {row.companyDetails?.name || '---'}
        </Typography>
      ),
    },
    {
      field: 'dateOfRegistration',
      headerName: t('common:dateOfRegistration'),
      headerAlign: 'center',
      minWidth: 140,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {row.companyDetails?.dateOfRegistration || '---'}
        </Typography>
      ),
    },
    {
      field: 'emails',
      headerName: t('common:email'),
      headerAlign: 'center',
      minWidth: 250,
      maxWidth: 550,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {Array.isArray(row.companyDetails?.emails) && row.companyDetails.emails.filter(Boolean).length > 0
            ? row.companyDetails.emails.filter(Boolean).join(', ')
            : '---'}
        </Typography>
      ),
    },
    {
      field: 'addresses',
      headerName: t('common:addresses'),
      headerAlign: 'center',
      minWidth: 200,
      maxWidth: 300,
      sortable: false,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {Array.isArray(row.companyDetails?.addresses) && row.companyDetails.addresses.length > 0
            ? row.companyDetails.addresses
                .map((addr: any) => {
                  const parts = [addr.street, addr.city].filter(Boolean)
                  return parts.length > 0 ? parts.join(' ') : null
                })
                .filter(Boolean)
                .join(', ') || '---'
            : '---'}
        </Typography>
      ),
    },
    {
      field: 'monitored',
      headerName: t('common:monitored'),
      headerAlign: 'center',
      minWidth: 100,
      maxWidth: 150,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {row.monitored ? t('common:yes') : t('common:no')}
        </Typography>
      ),
    },
    {
      field: 'cluster',
      headerName: t('common:cluster'),
      headerAlign: 'center',
      minWidth: 100,
      maxWidth: 150,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {row.cluster || '---'}
        </Typography>
      ),
    },
    {
      field: 'site',
      headerName: t('common:site'),
      headerAlign: 'center',
      minWidth: 120,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {row.site || '---'}
        </Typography>
      ),
    },
    {
      field: 'legalRepresentative',
      headerName: t('common:legalRepresentative'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 220,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {row.legalRepresentative || '---'}
        </Typography>
      ),
    },
    {
      field: 'legalRepresentativeEmail',
      headerName: t('common:legalRepresentativeEmail'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 250,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {row.legalRepresentativeEmail || '---'}
        </Typography>
      ),
    },
    {
      field: 'legalRepresentativePhone',
      headerName: t('common:legalRepresentativePhone'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 200,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {row.legalRepresentativePhone || '---'}
        </Typography>
      ),
    },
    {
      field: 'legalRepresentativeDocumentNumber',
      headerName: t('common:legalRepresentativeDocumentNumber'),
      headerAlign: 'center',
      minWidth: 180,
      maxWidth: 250,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {row.legalRepresentativeDocumentNumber?.includes('kyb.documentNumber.notFound') 
            ? `⚠️${t('messages:kyb.documentNumber.notFound')}⚠️` 
            : row.legalRepresentativeDocumentNumber || '---'
          }
        </Typography>
      ),
    },
    {
      field: 'clientTypes',
      headerName: t('common:clientTypes'),
      headerAlign: 'center',
      minWidth: 130,
      maxWidth: 180,
      sortable: false,
      renderCell: ({ row }) => {
        const items = (row.clientTypes || '').split(',').map((item: string) => item.trim()).filter(Boolean);
        return <ShowMoreListCell items={items} />;
      },
    },
    {
      field: 'businessSegments',
      headerName: t('common:businessSegments'),
      headerAlign: 'center',
      minWidth: 130,
      maxWidth: 180,
      sortable: false,
      renderCell: ({ row }) => {
        const items = (row.businessSegments || '').split(',').map((item: string) => item.trim()).filter(Boolean);
        return <ShowMoreListCell items={items} />;
      },
    },
    {
      field: 'products',
      headerName: t('common:products'),
      headerAlign: 'center',
      minWidth: 130,
      maxWidth: 180,
      sortable: false,
      renderCell: ({ row }) => {
        const items = (row.products || '').split(',').map((item: string) => item.trim()).filter(Boolean);
        return <ShowMoreListCell items={items} />;
      },
    },
    {
      field: 'metadata',
      headerName: t('messages:kyb.settings.metadata.title'),
      headerAlign: 'center',
      minWidth: 250,
      maxWidth: 350,
      sortable: false,
      renderCell: ({ row }) => <MetadataCell value={row.metadata} t={t} />,
    },
    {
      field: 'riskLevel',
      headerName: t('common:riskLevel'),
      headerAlign: 'center',
      minWidth: 200,
      maxWidth: 250,
      sortable: true,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <Tooltip
            title={
              row?.riskResult?.riskScoreProbability ? (
                <Box style={{ display: 'flex', flexDirection: 'column' }}>
                  <div>Impact result: {row?.riskResult?.riskScoreImpact || '---'}</div>
                  <div>Probability result: {row?.riskResult?.riskScoreProbability || '---'}</div>
                  <div>Score: {row.riskScore || '---'}</div>
                </Box>
              ) : (
                <div>Score: {row?.riskScore || '---'}</div>
              )
            }
            placement="right-end"
          >
            <Box>
              <RiskLevel type={row.riskLevel} />
            </Box>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: t('common:status'),
      headerAlign: 'center',
      minWidth: 200,
      maxWidth: 250,
      sortable: true,
      renderCell: ({ row }) => {
        if (!row.status) {
          return (
            <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
              ---
            </Typography>
          )
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Status type={row.status} noIcon={false} sizeBig={false} variant="default" />
          </Box>
        )
      },
    },
    {
      field: 'shareHolders',
      headerName: t('common:shareholders'),
      headerAlign: 'center',
      minWidth: 600,
      maxWidth: 700,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <ColumnKYC
            items={row.shareHolders}
            type="shareholders"
            companyDocumentNumber={row.companyDetails?.documentNumber}
          />
        </Box>
      ),
    },
    {
      field: 'relatedPersons',
      headerName: t('common:relatedPersons'),
      headerAlign: 'center',
      minWidth: 800,
      maxWidth: 900,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ textAlign: 'center', width: '100%' }}>
          <ColumnKYC
            items={row.relatedPersons}
            type="relatedPersons"
            companyDocumentNumber={row.companyDetails?.documentNumber}
          />
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: t('common:creationDate'),
      headerAlign: 'center',
      minWidth: 140,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {toLocaleUTCDateString(row.createdAt)}
        </Typography>
      ),
    },
  ]

  const defaultColumns = [
    'companyDetails.documentNumber',
    'companyDetails.name',
    'riskLevel',
    '_id',
    'status',
    'createdAt',
  ]

  const getFieldName = (col: TColumn) => {
    if (col.field === 'documentNumber') return 'companyDetails.documentNumber'
    if (col.field === 'name') return 'companyDetails.name'
    return col.field
  }

  if (currentCountry === 'BR') {
    return allColumns
  }
  return allColumns.filter((col) => defaultColumns.includes(getFieldName(col)))
}

export const useCommonColumnsKyc = (t: any, currentCountry: string) => {
  const allColumns: TColumn[] = [
    {
      field: 'documentNumber',
      headerName: t('common:documentNumber'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 200,
      sortable: true,
      renderCell: ({ row }) => {
        return (
          <Link
            title={row._id}
            component={ReactRouterLink}
            to={`/kyc/${currentCountry}/detail/${row._id}`}
            sx={{ width: '100%' }}
          >
            <Typography variant="body2" fontWeight="bold" sx={{ textAlign: 'center', width: '100%' }}>
              {row.documentNumber?.includes('kyb.documentNumber.notFound') 
                ? `⚠️${t('messages:kyb.documentNumber.notFound')}⚠️` 
                : row.documentNumber ?? '---'
              }
            </Typography>
          </Link>
        )
      },
    },
    {
      field: 'name',
      headerName: t('common:name'),
      headerAlign: 'center',
      minWidth: 250,
      maxWidth: 400,
      sortable: true,
      renderCell: ({ row }) => {
        return (
          <Typography variant="body2" title={row.name ?? '---'} sx={{ textAlign: 'center', width: '100%' }}>
            {row.name ?? '---'}
          </Typography>
        )
      },
    },
    {
      field: 'nationality',
      headerName: t('common:nationality'),
      headerAlign: 'center',
      minWidth: 100,
      maxWidth: 150,
      sortable: true,
      renderCell: ({ row }) => {
        return (
          <Box sx={{ textAlign: 'center', width: '100%' }}>
            {Flags(row.nationality === 'Brazil' ? 'BR' : row.nationality)}
          </Box>
        )
      },
    },
    {
      field: 'email',
      headerName: t('common:email'),
      headerAlign: 'center',
      minWidth: 200,
      maxWidth: 300,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.email) {
          return (
            <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
              {row.email}
            </Typography>
          )
        } else if (row.emailList?.length > 0) {
          return (
            <Tooltip title={row.emailList.map((e: any) => e.email).join(', ')}>
              <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
                {row.emailList[0].email}
              </Typography>
            </Tooltip>
          )
        }
        return (
          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
            ---
          </Typography>
        )
      },
    },
    {
      field: 'dateOfBirth',
      headerName: t('common:dateOfBirth'),
      headerAlign: 'center',
      minWidth: 140,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => {
        return row.dateOfBirth ? (
          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
            {row.dateOfBirth}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
            ---
          </Typography>
        )
      },
    },
    {
      field: 'addresses',
      headerName: t('common:address'),
      headerAlign: 'center',
      minWidth: 250,
      maxWidth: 400,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.addresses?.length > 0) {
          const addr = row.addresses[0]
          const streetNumber = [addr.street, addr.number].filter(Boolean).join(', ') || '---'
          const cityState = [addr.city, addr.state].filter(Boolean).join('/') || ''
          const tooltipText = cityState ? `${streetNumber} - ${cityState}` : streetNumber

          return (
            <Tooltip title={tooltipText}>
              <Typography
                variant="body2"
                sx={{ textAlign: 'center', width: '100%' }}
              >{streetNumber}</Typography>
            </Tooltip>
          )
        }
        return (
          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
            ---
          </Typography>
        )
      },
    },
    {
      field: 'monitored',
      headerName: t('common:monitored'),
      headerAlign: 'center',
      minWidth: 100,
      maxWidth: 150,
      sortable: true,
      renderCell: ({ row }) => {
        return row.monitored ? (
          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
            {t('common:yes')}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
            {t('common:no')}
          </Typography>
        )
      },
    },
    {
      field: 'lastResetDate',
      headerName: t('common:lastResetDate'),
      headerAlign: 'center',
      minWidth: 140,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => {
        return row.lastResetDate ? (
          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
            {buildTimeStringFromTimestamp(row.lastResetDate)}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
            ---
          </Typography>
        )
      },
    },
    {
      field: 'riskLevel',
      headerName: t('common:riskLevel'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 200,
      sortable: true,
      renderCell: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Tooltip
              title={
                row?.riskResult ? (
                  <Box style={{ display: 'flex', flexDirection: 'column' }}>
                    {row?.riskResult?.data && <div>Data: {JSON.stringify(row.riskResult.data)}</div>}
                    <div>Score: {row.riskScore || '---'}</div>
                  </Box>
                ) : (
                  <div>Score: {row?.riskScore || '---'}</div>
                )
              }
              placement="right-end"
            >
              <Box>
                <RiskLevel type={row.riskLevel} />
              </Box>
            </Tooltip>
          </Box>
        )
      },
    },
    {
      field: 'status',
      headerName: t('common:status'),
      headerAlign: 'center',
      minWidth: 150,
      maxWidth: 200,
      sortable: true,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Status type={params.row.status} noIcon={false} sizeBig={false} variant="default" />
          </Box>
        )
      },
    },
    {
      field: 'createdAt',
      headerName: t('common:creationDate'),
      headerAlign: 'center',
      minWidth: 140,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {buildTimeStringFromTimestamp(row.createdAt)}
        </Typography>
      ),
    },
    {
      field: 'updatedAt',
      headerName: t('common:lastUpdated'),
      headerAlign: 'center',
      minWidth: 140,
      maxWidth: 180,
      sortable: true,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ textAlign: 'center', width: '100%' }}>
          {buildTimeStringFromTimestamp(row.updatedAt)}
        </Typography>
      ),
    },
  ]

  const defaultColumns = ['documentNumber', 'name', 'riskLevel', 'status', 'createdAt']

  if (currentCountry === 'BR') {
    return allColumns
  }
  return allColumns.filter((col) => defaultColumns.includes(col.field))
}

export const useCommonColumnsKybDefault = () => {
  const { t } = useTranslation(['common', 'messages'])
  const { currentCountry } = useSelector((state: any) => state.kybCountry)
  return [
    {
      field: 'documentNumber',
      headerName: t('common:documentNumber'),
      flex: 3,
      renderCell: ({ row }: any) => (
        <Link component={ReactRouterLink} title={row._id} to={`/kyb/${currentCountry}/detail/${row._id}`}>
          <Typography variant="body2">
            {row.companyDetails?.documentNumber?.includes('kyb.documentNumber.notFound') 
              ? `⚠️${t('messages:kyb.documentNumber.notFound')}⚠️` 
              : row.companyDetails?.documentNumber ?? '---'
            }
          </Typography>
        </Link>
      ),
    },
    {
      field: 'name',
      headerName: t('common:companyName'),
      flex: 6,
      renderCell: ({ row }: any) => <Typography variant="body2">{row.companyDetails?.name ?? '---'}</Typography>,
    },
    {
      field: 'riskLevel',
      headerName: t('common:riskLevel'),
      flex: 3,
      renderCell: ({ row }: any) => (
        <Tooltip
          title={
            row?.riskResult?.riskScoreProbability ? (
              <Box style={{ display: 'flex', flexDirection: 'column' }}>
                <div>Impact result: {row?.riskResult?.riskScoreImpact || '---'}</div>
                <div>Probability result: {row?.riskResult?.riskScoreProbability || '---'}</div>
                <div>Score: {row.riskScore || '---'}</div>
              </Box>
            ) : (
              <div>Score: {row?.riskScore || '---'}</div>
            )
          }
          placement="right-end"
        >
          <Box>
            <RiskLevel type={row.riskLevel} />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'status',
      headerName: t('common:status'),
      flex: 3,
      renderCell: ({ row }: any) => <Status type={row.status} noIcon={false} sizeBig={false} variant="default" />,
    },
    {
      field: 'createdAt',
      headerName: t('common:creationDate'),
      flex: 3,
      renderCell: ({ row }: any) => <Typography variant="body2">{toLocaleUTCDateString(row.createdAt)}</Typography>,
    },
  ]
}

export const useCommonColumnsKycDefault = () => {
  const { t } = useTranslation(['common', 'messages'])
  const { currentCountry } = useSelector((state: any) => state.kybCountry)
  return [
    {
      field: 'documentNumber',
      headerName: t('common:documentNumber'),
      flex: 3,
      renderCell: ({ row }: any) => (
        <Link title={row._id} component={ReactRouterLink} to={`/kyc/${currentCountry}/detail/${row._id}`}>
          <Typography variant="body2">
            {row.documentNumber?.includes('kyb.documentNumber.notFound') 
              ? `⚠️${t('messages:kyb.documentNumber.notFound')}⚠️` 
              : row.documentNumber ?? '---'
            }
          </Typography>
        </Link>
      ),
    },
    {
      field: 'name',
      headerName: t('common:name'),
      flex: 6,
      renderCell: ({ row }: any) => <p title={row.name ?? '---'}>{row.name ?? '---'}</p>,
    },
    {
      field: 'nationality',
      headerName: t('common:nationality'),
      flex: 2,
      renderCell: ({ row }: any) => Flags(row.nationality === 'Brazil' ? 'BR' : row.nationality),
    },
    {
      field: 'riskLevel',
      headerName: t('common:riskLevel'),
      flex: 3,
      renderCell: ({ row }: any) => (
        <Tooltip
          title={
            row?.riskResult?.riskScoreProbability ? (
              <Box style={{ display: 'flex', flexDirection: 'column' }}>
                <div>Impact result: {row?.riskResult?.riskScoreImpact || '---'}</div>
                <div>Probability result: {row?.riskResult?.riskScoreProbability || '---'}</div>
                <div>Score: {row.riskScore || '---'}</div>
              </Box>
            ) : (
              <div>Score: {row?.riskScore || '---'}</div>
            )
          }
          placement="right-end"
        >
          <Box>
            <RiskLevel type={row.riskLevel} />
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'creationOrigin',
      headerName: t('common:origin'),
      flex: 2,
      renderCell: ({ row }: any) => {
        const option = kybCreationOriginOptions.find((option) => option.value === row.creationOrigin)
        return option ? t(`common:${option.label}`) : '---'
      },
    },
    {
      field: 'status',
      headerName: t('common:status'),
      flex: 3,
      renderCell: (params: any) => <Status type={params.row.status} noIcon={false} sizeBig={false} variant="default" />,
    },
    {
      field: 'createdAt',
      headerName: t('common:creationDate'),
      flex: 3,
      renderCell: ({ row }: any) => (
        <Typography variant="body2">{buildTimeStringFromTimestamp(row.createdAt)}</Typography>
      ),
    },
  ]
}
